import { useMemo } from 'react';

import type { RegisterOptions } from 'react-hook-form';
import type { ZodType } from 'zod';

import { zodRule } from './zodRule';

type ValidateMap = Extract<RegisterOptions['validate'], Record<string, unknown>>;

type UseMergedRulesOptions = {
  rules?: RegisterOptions;
  maxLength?: number;
  // Validated alongside `rules`, for fields that state their own constraints inline.
  schema?: ZodType;
  // Custom length function — e.g. strip HTML before counting. Defaults to `value.length`.
  getLength?: (value: string) => number;
};

// Adds a silent maxTextLength validator; the visible over-limit caption is rendered by
// RecommendedLengthHint. A `schema` joins the same validate map under its own key.
export const useMergedRules = ({
  rules,
  maxLength,
  schema,
  getLength,
}: UseMergedRulesOptions): RegisterOptions | undefined =>
  useMemo<RegisterOptions | undefined>(() => {
    if (!(maxLength || schema)) {
      return rules;
    }
    const measure = getLength ?? ((value: string) => value.length);
    const validate: ValidateMap = {
      ...(typeof rules?.validate === 'function' ? { custom: rules.validate } : rules?.validate),
    };

    if (schema) {
      validate.schema = zodRule(schema);
    }

    if (maxLength) {
      validate.maxTextLength = (value: string) => measure(value ?? '') <= maxLength;
    }

    return { ...rules, validate };
  }, [rules, maxLength, schema, getLength]);
